import type {
  ProgramTree,
  WeekPlanOutput,
  WorkoutExerciseInput,
  WorkoutExerciseRow,
  WorkoutRow,
} from '@st/contract';

/** Tjedan uvijek ima 7 dana; 0 = ponedjeljak. */
export const DAY_INDEXES = [0, 1, 2, 3, 4, 5, 6] as const;

export const DAY_KEYS = [
  'builder.mon',
  'builder.tue',
  'builder.wed',
  'builder.thu',
  'builder.fri',
  'builder.sat',
  'builder.sun',
] as const;

export const WORKOUT_TYPES = ['strength', 'cardio', 'mobility', 'rest', 'mixed'] as const;
export type WorkoutType = (typeof WORKOUT_TYPES)[number];

/**
 * Redak stabla → ulaz za `PUT /workouts/:id/exercises`.
 *
 * Ključno: ruta zamjenjuje CIJELU listu i redoslijed čita iz pozicije u nizu,
 * pa se `id` i `sortOrder` namjerno ne šalju — server ih izdaje iznova.
 */
export function toExerciseInput(row: WorkoutExerciseRow): WorkoutExerciseInput {
  return {
    exerciseId: row.exerciseId,
    sets: row.sets,
    reps: row.reps,
    weightHintKg: row.weightHintKg,
    restSeconds: row.restSeconds,
    tempo: row.tempo,
    trainerNoteHr: row.trainerNoteHr,
    trainerNoteEn: row.trainerNoteEn,
    supersetGroup: row.supersetGroup,
    prescribedSets: row.prescribedSets.map((p) => ({
      reps: p.reps,
      weightKg: p.weightKg,
      restSeconds: p.restSeconds,
      note: p.note,
    })),
  };
}

/** Pomak jednog elementa; izvan granica vraća isti niz (nema mutacije). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return [...list];
  }
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return [...list];
  next.splice(to, 0, item);
  return next;
}

/** Nova kopija stabla s promijenjenom listom vježbi jednog treninga (optimistični upis). */
export function withWorkoutExercises(
  tree: ProgramTree,
  workoutId: string,
  exercises: readonly WorkoutExerciseRow[],
): ProgramTree {
  return {
    ...tree,
    weeks: tree.weeks.map((w) => ({
      ...w,
      workouts: w.workouts.map((wo) =>
        wo.id === workoutId
          ? { ...wo, exercises: exercises.map((e, i) => ({ ...e, sortOrder: i })) }
          : wo,
      ),
    })),
  };
}

/** Nova kopija stabla s promijenjenim poljima jednog treninga (preimenovanje). */
export function withWorkoutPatch(
  tree: ProgramTree,
  workoutId: string,
  patch: Partial<WorkoutRow>,
): ProgramTree {
  return {
    ...tree,
    weeks: tree.weeks.map((w) => ({
      ...w,
      workouts: w.workouts.map((wo) => (wo.id === workoutId ? { ...wo, ...patch } : wo)),
    })),
  };
}

export function findWorkout(tree: ProgramTree, workoutId: string): WorkoutRow | null {
  for (const week of tree.weeks) {
    const found = week.workouts.find((w) => w.id === workoutId);
    if (found) return found;
  }
  return null;
}

/** Dani koji u tjednu još nemaju trening — dupliciranje i premještanje trebaju ovo. */
export function freeDays(workouts: readonly WorkoutRow[]): number[] {
  const taken = new Set(workouts.map((w) => w.dayIndex));
  return DAY_INDEXES.filter((d) => !taken.has(d));
}

export function exerciseName(
  library: readonly { id: string; nameHr: string; nameEn: string }[],
  exerciseId: string,
  locale: 'hr' | 'en',
): string | null {
  const found = library.find((e) => e.id === exerciseId);
  if (!found) return null;
  return locale === 'en' ? found.nameEn : found.nameHr;
}

/** Sažetak propisa za kompaktni prikaz: "4 × 10 · 90 s". */
export function prescriptionSummary(row: WorkoutExerciseRow): string {
  const parts: string[] = [];
  if (row.prescribedSets.length > 0) {
    parts.push(row.prescribedSets.map((p) => p.reps ?? '?').join(' / '));
  } else if (row.sets && row.reps) {
    parts.push(`${row.sets} × ${row.reps}`);
  } else if (row.sets) {
    parts.push(`${row.sets} ×`);
  } else if (row.reps) {
    parts.push(row.reps);
  }
  if (row.restSeconds !== null) parts.push(`${row.restSeconds} s`);
  if (row.tempo) parts.push(row.tempo);
  return parts.join(' · ');
}

/** Prijevodni ključ za tip treninga; jedan popis za builder i za plan klijenta. */
export const WORKOUT_TYPE_KEY: Record<WorkoutType, string> = {
  strength: 'builder.typeStrength',
  cardio: 'builder.typeCardio',
  mobility: 'builder.typeMobility',
  rest: 'builder.typeRest',
  mixed: 'builder.typeMixed',
};

export interface PlanWorkoutRow {
  workoutId: string;
  name: string;
  scheduledOn: string;
  status: 'planned' | 'done' | 'missed';
  /** Prijevodni ključ tipa treninga; null kad API tip ne pošalje. */
  typeLabel: string | null;
}

/**
 * `GET /v1/training/plan` vraća sedam dana u tjednu, uključujući prazne.
 * Ekran plana treba samo dane s treningom, poredane po datumu — ovo je jedina
 * pretvorba, da je ne rade i plan i kalendar svaki na svoj način.
 */
export function planWorkouts(plan: WeekPlanOutput | undefined): PlanWorkoutRow[] {
  if (!plan) return [];
  return plan.days
    .filter((d): d is typeof d & { workoutId: string } => Boolean(d.workoutId) && d.type !== 'rest')
    .map((d) => ({
      workoutId: d.workoutId,
      name: d.name ?? '',
      scheduledOn: d.scheduledOn,
      status: d.completed ? ('done' as const) : d.missed ? ('missed' as const) : ('planned' as const),
      typeLabel: d.type && isWorkoutType(d.type) ? WORKOUT_TYPE_KEY[d.type] : null,
    }))
    .sort((a, b) => a.scheduledOn.localeCompare(b.scheduledOn));
}

function isWorkoutType(value: string): value is WorkoutType {
  return (WORKOUT_TYPES as readonly string[]).includes(value);
}
